import { Card, Divider, Group, Stack, Tooltip } from '@mantine/core';
import type { AutomationAdminOut } from '@repo/api-codegen';
import { useApiAdminRemoveAutomationFromQueue } from '@repo/api-codegen';
import { Badge, Button, Icon, Text, Title } from '@repo/foundations';
import dayjs from 'dayjs';
import { useState } from 'react';
import { formatEntityDateTime } from '../../../utils/time';
import DescriptionGroup from '../BackgroundJobPage/DescriptionGroup';
import { useStyles as useParentStyles } from '../IntegrationsStatusPage/IntegrationsStatusPage.styles';

interface AutomationCardProps {
	automation: AutomationAdminOut;
	onRefresh?: () => void;
}

function AutomationCard({ automation, onRefresh }: AutomationCardProps) {
	const { classes: parentClasses } = useParentStyles();
	const [expanded, setExpanded] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const { mutateAsync: removeFromQueue } =
		useApiAdminRemoveAutomationFromQueue();

	// Calculate the next run in relative time
	const nextRunAtRelative = automation.next_run_at
		? dayjs(automation.next_run_at).fromNow()
		: 'Unknown';

	// Format the next run date for the tooltip
	const nextRunDate = automation.next_run_at
		? formatEntityDateTime(automation.next_run_at)
		: 'Not scheduled';

	// Determine the status badge
	const isEnabled = automation.is_enabled;

	// Format cache entry timestamp
	const cacheEntry = automation.cache_entry;
	const isHeartbeat = cacheEntry > 0;
	const timestamp = Math.abs(cacheEntry);
	const cacheTimestamp = dayjs.unix(timestamp).toDate();
	const formattedCacheTime = formatEntityDateTime(cacheTimestamp.toISOString());
	const cacheDescription = isHeartbeat
		? `Last heartbeat: ${formattedCacheTime}`
		: `Scheduled: ${formattedCacheTime}`;
	const cacheTimeSince = dayjs.unix(timestamp).fromNow();

	const handleRemoveFromQueue = () => {
		if (
			typeof window !== 'undefined' &&
			window.confirm(
				`Are you sure you want to remove "${automation.title || 'Unnamed Automation'}" from the active queue? This will only remove it from the Redis heartbeat queue, it will not stop any running processes.`
			)
		) {
			setIsRemoving(true);
			removeFromQueue({
				queryParams: {
					automation_id: automation.id,
				},
			})
				.then(() => {
					if (onRefresh) {
						onRefresh();
					}
				})
				.catch(() => {
					alert('Failed to remove automation from queue');
				})
				.finally(() => {
					setIsRemoving(false);
				});
		}
	};

	return (
		<Card className={parentClasses.card}>
			<Stack w="100%">
				<Group position="apart">
					<Group>
						<Title size="xl">{automation.title || 'Unnamed Automation'}</Title>
						<Group>
							{isEnabled ? (
								<Badge variant="success">Enabled</Badge>
							) : (
								<Badge variant="default">Disabled</Badge>
							)}
							<Badge variant={isHeartbeat ? 'success' : 'info'}>
								{isHeartbeat ? 'Active' : 'Scheduled'}
							</Badge>
							<Tooltip label={nextRunDate} position="bottom" withArrow>
								<Badge variant="info">Next Run: {nextRunAtRelative}</Badge>
							</Tooltip>
							<Text size="sm" color="text/secondary/default">
								ID: {automation.id}
							</Text>
						</Group>
					</Group>
					<Group>
						<Button
							onClick={handleRemoveFromQueue}
							variant="default"
							loading={isRemoving}
							color="red"
						>
							Remove from Queue
						</Button>
						<Button onClick={() => setExpanded(!expanded)}>
							<Icon name={expanded ? 'chevronDown' : 'chevronRight'} />
						</Button>
					</Group>
				</Group>

				{automation.description && (
					<Text size="sm" color="text/secondary/default">
						{automation.description}
					</Text>
				)}

				<Group spacing="md">
					<Text
						size="sm"
						color={isHeartbeat ? 'text/success/default' : 'text/info/default'}
					>
						{cacheDescription} ({cacheTimeSince})
					</Text>

					<Text size="sm" color="text/primary/default">
						Workspace: {automation.workspace_name} ({automation.workspace_id})
					</Text>
				</Group>
			</Stack>

			{expanded && (
				<>
					<Divider my="sm" />
					<Title order={2} pb="sm">
						Details
					</Title>
					<Group w="100%" position="apart" pb="md">
						<DescriptionGroup
							title="Automation ID"
							description={automation.id}
						/>
						<DescriptionGroup
							title="Workspace"
							description={`${automation.workspace_name} (${automation.workspace_id})`}
						/>
						<DescriptionGroup
							title="Next scheduled run"
							description={formatEntityDateTime(automation.next_run_at || '')}
						/>
					</Group>
					<Group w="100%" position="apart" pb="md">
						<DescriptionGroup
							title="Cache entry type"
							description={
								isHeartbeat ? 'Last heartbeat time' : 'Scheduled time'
							}
						/>
						<DescriptionGroup
							title="Cache timestamp"
							description={`${formattedCacheTime} (${cacheTimeSince})`}
						/>
						<DescriptionGroup
							title="Raw cache value"
							description={automation.cache_entry.toString()}
						/>
					</Group>
					<Group w="100%" position="apart" pb="md">
						<DescriptionGroup
							title="Last run at"
							description={
								automation.last_run_at
									? formatEntityDateTime(automation.last_run_at)
									: 'Never'
							}
						/>
						<DescriptionGroup
							title="Last scheduled at"
							description={
								automation.last_scheduled_at
									? formatEntityDateTime(automation.last_scheduled_at)
									: 'Never'
							}
						/>
					</Group>
				</>
			)}
		</Card>
	);
}

export default AutomationCard;
